import { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Grid,
  Button,
  Stack,
  Typography,
  Autocomplete,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { client, MarketCategory, Instance } from '../../app/services/api/orderManagementClient';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { MATERIAL_ICON_OPTIONS, resolveMaterialIcon } from '../orderly-for-distributors/ItemTags/components/itemTagUtils';

const CategoryForm = () => {
  const { t } = useTranslation('categories');
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const isEdit = Boolean(categoryId);
  const [searchParams] = useSearchParams();
  
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [availableCategories, setAvailableCategories] = useState<MarketCategory[]>([]);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);

  const companyId = searchParams.get('companyId');
  const searchTerm = searchParams.get('searchTerm');
  
  const [formData, setFormData] = useState<MarketCategory>({
    categoryId: 0,
    parentId: -2,
    parentName: '',
    name: '',
    companyId: 0,
    instanceName: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    return params;
  };

  const fetchInstances = async () => {
    try {
      const result = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (result.success && result.data) {
        setInstances(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching instances:', error);
    }
  };

  const fetchCategoriesForInstance = async (companyId: number) => {
    if (!companyId) {
      setAvailableCategories([]);
      return;
    }

    try {
      const result = await client.getMarketCategories({
        companyId: companyId.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
        sort: '',
        direction: 'asc',
      });
      
      if (result.success && result.data) {
        // Filter out the current category to prevent circular references
        const categories = result.data.items.filter(cat => 
          !isEdit || cat.categoryId !== parseInt(categoryId!),
        );
        setAvailableCategories(categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setAvailableCategories([]);
    }
  };

  const fetchCategory = async () => {
    if (!categoryId) return;

    setIsLoading(true);
    try {
      const result = await client.getMarketCategory(categoryId);
      if (result.success && result.data) {
        setFormData(result.data);
        await fetchCategoriesForInstance(result.data.companyId);
      }
    } catch (error) {
      console.error('Error fetching category:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInstances();
    if (isEdit) {
      fetchCategory();
    }
  }, [categoryId, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('categoryNameRequired');
    }

    if (!formData.companyId) {
      newErrors.companyId = t('instanceRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof MarketCategory) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    const newCompanyId = value?.id || 0;
    setFormData(prev => ({
      ...prev,
      companyId: newCompanyId,
      parentId: -2, // Reset parent category when instance changes
    }));
    
    if (newCompanyId) {
      fetchCategoriesForInstance(newCompanyId);
    } else {
      setAvailableCategories([]);
    }
    
    if (errors.companyId) {
      setErrors(prev => ({
        ...prev,
        companyId: '',
      }));
    }
  };

  const handleParentCategoryChange = (event: React.SyntheticEvent, value: MarketCategory | null) => {
    setFormData(prev => ({
      ...prev,
      parentId: value?.categoryId || -2,
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (isEdit) {
        const result = await client.updateMarketCategory(categoryId!, formData);
        if (result.success) {
          setMessage(t('updateSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/categories?${params.toString()}`);
          }, 500);
        }
      } else {
        const result = await client.createMarketCategory(formData);
        if (result.success) {
          setMessage(t('createSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/categories?${params.toString()}`);
          }, 500);
        }
      }
    } catch (error) {
      console.error('Error saving category:', error);
      setMessage(isEdit ? t('updateErrorMessage') : t('createErrorMessage'));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    const params = getUrlParams();
    navigate(`/categories?${params.toString()}`);
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'CategoryUpdate' : 'CategoryCreate', 'ConfigurationAdmin']);

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{isEdit ? t('editCategory') : t('createCategory')}</PageTitle>
        <Typography>{t('loading')}</Typography>
      </PageArea>
    );
  }

  const getSelectedParentCategory = () => {
    if (formData.parentId <= 0) return null;
    return availableCategories.find(cat => cat.categoryId === formData.parentId) || null;
  };

  return (
    <PageArea>
      <PageTitle>{isEdit ? t('editCategory') : t('createCategory')}</PageTitle>
      
      <Stack spacing={8}>
        {/* Basic Category Information Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('basicInformation')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Grid 
              container 
              spacing={8}>
              <Grid 
                item 
                xs={12} 
                md={6}>
                <TextField
                  fullWidth
                  label={t('categoryName')}
                  placeholder={t('categoryNamePlaceholder')}
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid 
                item
                xs={12}
                md={6}>
                <FormControl
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <InputLabel>{t('categoryIcon')}</InputLabel>
                  <Select
                    value={formData.iconKey ?? ''}
                    label={t('categoryIcon')}
                    onChange={(e) => setFormData(prev => ({ ...prev, iconKey: e.target.value }))}
                    renderValue={(value) => {
                      if (!value) return <Typography variant='textSM'>{t('noIcon')}</Typography>;
                      const option = MATERIAL_ICON_OPTIONS.find(o => o.key === value);
                      const IconComponent = resolveMaterialIcon(value as string);
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconComponent sx={{ fontSize: 20 }} />
                          <Typography variant='textSM'>{option?.label ?? value}</Typography>
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value="">
                      <Typography variant='textSM'>{t('noIcon')}</Typography>
                    </MenuItem>
                    {MATERIAL_ICON_OPTIONS.map(({ key, label, Icon }) => (
                      <MenuItem
                        key={key}
                        value={key}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Icon sx={{ fontSize: 20 }} />
                          <Typography variant='textSM'>{label}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid 
                item 
                xs={12}
                md={6}>
                <Autocomplete
                  options={instances}
                  getOptionLabel={(option) => option.name}
                  value={instances.find(inst => inst.id === formData.companyId) || null}
                  onChange={handleInstanceChange}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('instance')}
                      placeholder={t('instancePlaceholder')}
                      error={!!errors.companyId}
                      helperText={errors.companyId}
                      required
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                  noOptionsText={t('noInstancesFound')}
                  clearOnBlur
                  selectOnFocus
                  handleHomeEndKeys
                  sx={{
                    '& .MuiAutocomplete-inputRoot': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid 
                item 
                xs={12}>
                <Autocomplete
                  options={availableCategories}
                  getOptionLabel={(option) => option.name}
                  value={getSelectedParentCategory()}
                  onChange={handleParentCategoryChange}
                  isOptionEqualToValue={(option, value) => option.categoryId === value.categoryId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('parentCategory')}
                      placeholder={t('parentCategoryPlaceholder')}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                  noOptionsText={t('noCategoriesFoundForSelection')}
                  clearOnBlur
                  selectOnFocus
                  handleHomeEndKeys
                  sx={{
                    '& .MuiAutocomplete-inputRoot': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Action Buttons Card */}
        <Card
          elevation={0}
          sx={{ backgroundColor: 'background.default' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
            >
              <Button
                variant="secondary"
                size="lg"
                onClick={handleCancel}
              >
                {t('cancel')}
              </Button>
              {userCanSave && (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleSubmit}
                  disabled={isSaving}
                >
                  {isSaving ? t('saving') : (isEdit ? t('update') : t('create'))}
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default CategoryForm;
